import { PlanMultidayResponse, Unplanned } from './route-optimization.interface';

/**
 * Mezcla de un recálculo acotado con el plan anterior.
 *
 * Replanificar solo los días afectados es exacto, no una aproximación: el
 * planificador es greedy hacia adelante —nunca revisa un día ya resuelto— y el
 * motor es determinista, así que un día conservado es idéntico al que devolvería
 * una corrida completa. Lo que sí hay que corregir es el **veredicto** de la
 * corrida acotada sobre los clientes que no le tocaban: el request acotado sigue
 * llevándolos a todos, así que el motor reporta como "sin día" a cualquiera cuyo
 * día quedó fuera del horizonte pedido. Ese resultado es del recorte, no del plan.
 *
 * Módulo puro: sin estado ni HTTP.
 */

export interface PartialMergeContext {
  /** Códigos de día (1..7) que sí se recalcularon. */
  recalculatedDays: readonly number[];
  /**
   * Días elegibles vigentes de un cliente. Vacío significa que solo una
   * asignación manual puede darle día, y esas viven dentro del rango.
   */
  daysOf: (clientId: string) => readonly number[];
}

export function mergePartialPlan(
  previous: PlanMultidayResponse | undefined,
  partial: PlanMultidayResponse,
  ctx: PartialMergeContext,
): PlanMultidayResponse {
  const recalculated = new Set(ctx.recalculatedDays ?? []);
  if (!previous || !recalculated.size) return partial;

  const recalculatedDates = new Set((partial.days ?? []).map((d) => d.date));
  const kept = (previous.days ?? []).filter(
    (d) => !recalculatedDates.has(d.date) && !recalculated.has(weekdayCodeOf(d.date)),
  );
  const days = [...kept, ...(partial.days ?? [])].sort((a, b) => a.date.localeCompare(b.date));

  const planned = new Set<string>();
  for (const d of days) {
    for (const route of d.routes ?? []) {
      for (const stop of route.stops ?? []) planned.add(stop.order_id);
    }
  }

  // Quien se recalculó responde con su veredicto nuevo; el resto conserva el
  // anterior, porque esta corrida no tenía cómo opinar sobre él.
  const inScope = (id: string): boolean => {
    const own = ctx.daysOf(id) ?? [];
    return !own.length || own.some((d) => recalculated.has(d));
  };
  const prevById = new Map((previous.unplanned ?? []).map((u) => [u.order_id, u]));
  const partialById = new Map((partial.unplanned ?? []).map((u) => [u.order_id, u]));

  const unplanned: Unplanned[] = [];
  for (const id of new Set([...prevById.keys(), ...partialById.keys()])) {
    if (planned.has(id)) continue;
    const entry = inScope(id)
      ? (partialById.get(id) ?? prevById.get(id))
      : (prevById.get(id) ?? partialById.get(id));
    if (entry) unplanned.push(entry);
  }

  return { ...partial, days, unplanned };
}

/** Código de día (1 = lunes … 7 = domingo) de una fecha `YYYY-MM-DD`. */
function weekdayCodeOf(date: string): number {
  const js = new Date(`${date}T00:00:00`).getDay(); // 0 = domingo
  return js === 0 ? 7 : js;
}
